"use client";
import { useState, useRef, useEffect, useCallback } from "react";
import { useRouter, usePathname, useSearchParams } from "next/navigation";
import Image from "next/image";
import Link from "next/link";
import { apiGetAllKeywords, apiSearchResult } from "@/apis/user";
import { getCategoryHref, getMerchantHref } from "@/constants/hooks";
import { SearchCategories, SearchMerchant } from "@/services/dataTypes";
import { FaStore, FaTags, FaListUl, FaSearch, FaTimes } from "react-icons/fa";

interface Props {
  companyId: string;
  mer_slug: string;
  slug_type: string;
  cat_slug: string;
}

let timeoutId: any = null;

const NavSearch = ({ companyId, mer_slug, slug_type, cat_slug }: Props) => {
  const router = useRouter();
  const pathName = usePathname();
  const searchParams = useSearchParams();
  const params = new URLSearchParams(searchParams?.toString());
  const [search, setSearch] = useState<string>(params.get("query") || "");
  const [tagsData, setTagsData] = useState<string[]>([]);
  const [categoriesData, setCategoriesData] = useState<SearchCategories[]>([]);
  const [merchantData, setMerchantData] = useState<SearchMerchant[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside the whole component
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleKeywords = async () => {
      const response = await apiGetAllKeywords(companyId);
      if (response?.data) setTagsData(response.data);
    };
    handleKeywords();
  }, [companyId]);

  const handleSearch = useCallback(async () => {
    try {
      if (search.trim().length >= 3) {
        const response = await apiSearchResult(search, companyId);
        setCategoriesData(response.data?.categories || []);
        setMerchantData(response.data?.merchants || []);
        if (pathName !== "/search") {
          setIsOpen(true);
        }
      }
    } catch (error) {
      console.error("Search error:", error);
    }
  }, [search, companyId, pathName]);

  useEffect(() => {
    if (timeoutId) clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      if (search.trim() === "") {
        setMerchantData([]);
        setCategoriesData([]);
      } else {
        handleSearch();
      }
    }, 500);
  }, [search]);

  const clearSearch = () => {
    setSearch("");
    setIsOpen(false);
  };

  const onFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (search.trim()) {
      setIsOpen(false);
      inputRef.current?.blur();
      router.push(`/search?query=${search}`);
    }
  };

  // Open dropdown on input focus if there is data to show
  const handleInputFocus = () => {
    if (tagsData.length > 0 || merchantData.length > 0 || categoriesData.length > 0) {
      setIsOpen(true);
    }
  };

  const hasResults = tagsData.length > 0 || merchantData.length > 0 || categoriesData.length > 0;

  return (
    <div ref={containerRef} className="relative w-full flex items-center gap-2">

      {/* ── input form ── */}
      <form
        onSubmit={onFormSubmit}
        className="flex items-center flex-1 min-w-0 bg-white/[0.08] border border-white/[0.14] focus-within:border-white/30 focus-within:bg-white/[0.13] rounded-xl px-3 py-1.5 transition-all duration-300"
      >
        <FaSearch className="text-white/40 mr-2 text-[13px] shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={handleInputFocus}
          placeholder="Search stores or deals..."
          className="flex-1 bg-transparent outline-none text-[13px] text-white font-medium py-1.5 min-w-0 placeholder-white/35"
        />
        {search && (
          <button type="button" onClick={clearSearch} className="p-1 shrink-0">
            <FaTimes className="text-white/40 hover:text-white/70 text-[10px] transition-colors" />
          </button>
        )}
      </form>

      {/* ── search button outside ── */}
      <button
        type="button"
        onClick={onFormSubmit as any}
        className="shrink-0 h-9 px-4 bg-blue-600 hover:bg-blue-700 text-white text-[11px] font-black uppercase tracking-wider rounded-xl transition-all active:scale-95 shadow-lg shadow-blue-600/30"
      >
        Search
      </button>

      {/* ── dropdown ── */}
      {isOpen && hasResults && (
        <div className="absolute top-full right-0 mt-2 w-[500px] bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.18)] z-[999] max-h-[460px] overflow-y-auto border border-slate-100 animate-in fade-in zoom-in-95 duration-200"
          style={{ scrollbarWidth: "none" }}>
          <div className="p-4 space-y-5">

            {/* Merchants */}
            {merchantData.length > 0 && (
              <div>
                <p className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-[0.25em] mb-3 px-1">
                  <FaStore className="text-indigo-800" /> Top Merchants
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {merchantData.map((merchant, i) => (
                    <Link key={i} href={getMerchantHref(merchant, mer_slug, slug_type)}
                      onClick={() => setIsOpen(false)}
                      className="no-underline flex items-center gap-2.5 p-2.5 rounded-xl bg-zinc-50 border border-slate-100 hover:border-indigo-800/20 hover:bg-white transition-all group">
                      <div className="w-8 h-8 relative bg-white rounded-lg border border-slate-100 shrink-0 overflow-hidden">
                        <Image src={merchant.merchant_logo} alt={merchant.merchant_name} fill className="object-contain p-1" />
                      </div>
                      <span className="font-semibold text-slate-700 group-hover:text-indigo-800 truncate text-[12px] transition-colors">
                        {merchant.merchant_name}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Categories */}
            {categoriesData.length > 0 && (
              <div>
                <p className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-[0.25em] mb-3 px-1">
                  <FaListUl className="text-indigo-800" /> Categories
                </p>
                <div className="grid grid-cols-2 gap-1.5">
                  {categoriesData.map((category, i) => (
                    <Link key={i} href={getCategoryHref(category, cat_slug, slug_type)}
                      onClick={() => setIsOpen(false)}
                      className="no-underline flex items-center justify-between px-3 py-2 rounded-xl bg-zinc-50 hover:bg-indigo-800/5 border border-transparent hover:border-indigo-800/10 transition-all group">
                      <span className="font-medium text-slate-600 group-hover:text-indigo-800 text-[12px] transition-colors">
                        {category?.name}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Trending */}
            {tagsData.length > 0 && (
              <div className="border-t border-slate-100 pt-4">
                <p className="flex items-center gap-2 text-[10px] font-black text-slate-400 uppercase tracking-[0.25em] mb-3 px-1">
                  <FaTags className="text-indigo-800" /> Trending
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {tagsData.slice(0, 12).map((item, i) => (
                    <Link key={i} href={`/search?query=${item}`}
                      onClick={() => setIsOpen(false)}
                      className="no-underline text-slate-600 hover:text-indigo-800 px-3 py-1.5 bg-zinc-50 rounded-full text-[11px] font-medium border border-slate-200 hover:border-indigo-800/30 transition-all">
                      #{item}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default NavSearch;
